import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import TopBar from './TopBar';
import Sidebar from './Sidebar';
import InstructorPanel from '../InstructorPanel';
import { useSidebar } from '../../contexts/SidebarContext';
import { useAssistancePanel } from '../../contexts/AssistancePanelContext';
import { useViewportSize } from '../../hooks/useViewportSize';

const ASSISTANCE_WIDTH = 380;
const SIDEBAR_WIDTH = 268;
const MIN_MAIN_WIDTH = 640;
const DESKTOP_BREAKPOINT = 1024; // matches tailwind `lg:` and SidebarContext's isDesktop()

/**
 * DashboardLayout - Main application layout (Glass Cockpit redesign)
 *
 * LAYOUT MODEL:
 * - Left Sidebar: a real flex sibling (push layout) ONLY at the desktop
 *   breakpoint (1024px+), controlled by SidebarContext.isOpen. Below that,
 *   it's ALWAYS a fixed overlay drawer with its own backdrop and close
 *   button, controlled by a completely separate isMobileDrawerOpen state -
 *   the hamburger button in TopBar always opens this drawer below 1024px,
 *   regardless of isOpen or the Assistance panel's state. This re-evaluates
 *   on every resize/orientation change (SidebarContext's resize listener).
 * - The right Assistance panel becomes a real flex sibling (pushing/sharing
 *   width with main content) ONLY when there's still at least 640px left
 *   for the main column after the left sidebar (if open, desktop only) and
 *   the 380px panel are subtracted. This is computed from the live viewport
 *   width (not a fixed breakpoint), so it re-evaluates correctly on resize
 *   and orientation change, and never squeezes the checklist/main column
 *   below a usable width.
 * - Otherwise (including all tablet/mobile widths) the panel is a fixed
 *   overlay that doesn't push content, with a close affordance.
 * - Before falling back to the overlay, if the left sidebar is open (desktop
 *   only) and closing it alone would free up enough room for side-by-side,
 *   the sidebar is auto-collapsed first. Its prior state is restored as
 *   soon as the Assistance panel closes. This never touches the mobile
 *   drawer state.
 */
const DashboardLayout = (props) => {
  const children = props.children;
  const { isOpen: isSidebarOpen, open: openSidebar, close: closeSidebar, isMobileDrawerOpen, closeMobileDrawer } = useSidebar();
  const { isOpen: isAssistanceOpen } = useAssistancePanel();
  const { width: viewportWidth } = useViewportSize();
  const location = useLocation();

  // Remembers whether the sidebar was open right before the Assistance panel
  // opened, so it can be restored on close (null = panel currently closed).
  const sidebarStateBeforePanelRef = useRef(null);

  useEffect(() => {
    if (isAssistanceOpen) {
      if (sidebarStateBeforePanelRef.current === null) {
        sidebarStateBeforePanelRef.current = isSidebarOpen;
      }
      const widthWithSidebar = viewportWidth - (viewportWidth >= DESKTOP_BREAKPOINT && isSidebarOpen ? SIDEBAR_WIDTH : 0) - ASSISTANCE_WIDTH;
      const widthWithoutSidebar = viewportWidth - ASSISTANCE_WIDTH;
      if (isSidebarOpen && widthWithSidebar < MIN_MAIN_WIDTH && widthWithoutSidebar >= MIN_MAIN_WIDTH) {
        closeSidebar();
      }
    } else if (sidebarStateBeforePanelRef.current) {
      openSidebar();
      sidebarStateBeforePanelRef.current = null;
    } else {
      sidebarStateBeforePanelRef.current = null;
    }
  }, [isAssistanceOpen, isSidebarOpen, viewportWidth, openSidebar, closeSidebar]);

  // The mobile drawer is route-based navigation - always close it after the
  // user lands on a new page.
  useEffect(() => {
    closeMobileDrawer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const sidebarSpace = viewportWidth >= DESKTOP_BREAKPOINT && isSidebarOpen ? SIDEBAR_WIDTH : 0;
  const mainWidthIfSideBySide = viewportWidth - sidebarSpace - ASSISTANCE_WIDTH;
  const sideBySideMode = mainWidthIfSideBySide >= MIN_MAIN_WIDTH;

  return (
    <div className="h-screen flex flex-col bg-glass-bg" data-testid="dashboard-layout">
      <TopBar />

      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Left Sidebar - real flex sibling at desktop breakpoint+, hidden below it
            (the mobile drawer below takes over entirely below that point) */}
        <aside
          className={`
            hidden lg:block flex-shrink-0 overflow-hidden
            border-r border-glass-border-nav bg-glass-card
            transition-[width] duration-300 ease-in-out
            ${isSidebarOpen ? 'lg:w-[268px]' : 'w-0'}
          `}
          data-testid="sidebar-container"
        >
          <div className="w-[268px] h-full overflow-y-auto">
            <Sidebar />
          </div>
        </aside>

        {/* Mobile/tablet nav drawer - fixed overlay below the desktop breakpoint,
            completely independent of the desktop sidebar's isOpen state */}
        {isMobileDrawerOpen && (
          <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-sidebar-drawer">
            <div
              className="absolute inset-0 bg-black/50"
              onClick={closeMobileDrawer}
              data-testid="mobile-sidebar-backdrop"
            />
            <div
              className="absolute inset-y-0 left-0 w-[280px] max-w-[85vw] bg-glass-card border-r border-glass-border-nav overflow-y-auto"
              onClick={(e) => {
                // Close on any nav link tap, including links to the current
                // page (which don't trigger the pathname-change effect below).
                if (e.target.closest('a')) {
                  closeMobileDrawer();
                }
              }}
            >
              <div className="flex items-center justify-end p-2 border-b border-glass-border-nav">
                <button
                  onClick={closeMobileDrawer}
                  className="w-9 h-9 flex items-center justify-center rounded-gc-button text-glass-text-body hover:bg-glass-card transition-colors"
                  aria-label="Close menu"
                  data-testid="close-mobile-sidebar-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <Sidebar />
            </div>
          </div>
        )}

        {/* Main Content - fills remaining space, centres itself */}
        <main
          className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden"
          data-testid="main-content"
        >
          {children}
        </main>

        {/* Right Panel (In-Flight Assistance) -
            real flex sibling when there's room, fixed overlay otherwise */}
        <aside
          className={
            sideBySideMode
              ? `static flex-shrink-0 overflow-hidden bg-glass-card border-l border-glass-border-nav transition-[width] duration-300 ease-in-out ${isAssistanceOpen ? 'w-[380px]' : 'w-0'}`
              : `fixed inset-y-0 top-14 md:top-16 right-0 z-40 w-full sm:w-[380px] overflow-hidden bg-glass-card border-l border-glass-border-nav transform transition-transform duration-300 ease-in-out ${isAssistanceOpen ? 'translate-x-0' : 'translate-x-full'}`
          }
          data-testid="assistance-panel-container"
        >
          <div className={`h-full overflow-y-auto ${sideBySideMode ? 'w-[380px]' : 'w-full sm:w-[380px]'}`}>
            <InstructorPanel />
          </div>
        </aside>
      </div>
    </div>
  );
};

export default DashboardLayout;
